//#region IMPORTS
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { Dialog, Listbox, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  confirmHandler: (supplier: string) => void;
  closeHandler: () => void;
  stock_portal: string[];
  message?: string;
  title?: string;
}
//#endregion

export default function SupplierSelect({
  confirmHandler,
  closeHandler,
  stock_portal,
  message = "Message",
  title = "Title",
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Local state
  const [selectedSuppliers, setSelectedSuppliers] = useState<string>(
    stock_portal[0]
  );
  // Guard against double submits (each click fires a server request).
  const [submitting, setSubmitting] = useState<boolean>(false);

  const handleConfirm = () => {
    if (submitting) return;
    setSubmitting(true);
    confirmHandler(selectedSuppliers);
  };
  //#endregion

  //#region RENDER
  return (
    <div>
      <div>
        <div className="mt-3 text-center sm:mt-5">
          <Dialog.Title
            as="h3"
            className="text-base font-semibold leading-6 text-gray-900"
          >
            {title}
          </Dialog.Title>
          <div className="mt-2">
            <p className="text-sm text-gray-500">{message}</p>
          </div>
        </div>
      </div>
      <Listbox value={selectedSuppliers} onChange={setSelectedSuppliers}>
        {({ open }) => (
          <div className="relative mt-2">
            <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
              <span className="block truncate">{selectedSuppliers}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-xs">
                {stock_portal.map((stock_portal, index) => (
                  <Listbox.Option
                    key={index}
                    className={({ active }) =>
                      classNames(
                        active ? "bg-blue-50 text-white" : "text-gray-900",
                        "relative cursor-default select-none py-2 pl-3 pr-9"
                      )
                    }
                    value={stock_portal}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={classNames(
                            selected ? "font-bold" : "font-normal",
                            "block truncate"
                          )}
                        >
                          {stock_portal}
                        </span>

                        {selected ? (
                          <span
                            className={classNames(
                              active ? "text-white" : "text-blue-600",
                              "absolute inset-y-0 right-0 flex items-center pr-4"
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        )}
      </Listbox>
      <div className="mt-6 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          disabled={submitting}
          className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-red-600 sm:ml-3 sm:w-auto"
          onClick={handleConfirm}
        >
          {submitting ? "Loading…" : t("yes")}
        </button>
        <button
          type="button"
          disabled={submitting}
          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:mt-0 sm:w-auto"
          onClick={closeHandler}
        >
          {t("cancel")}
        </button>
      </div>
    </div>
  );
  //#endregion
}
